/* eslint-disable camelcase */
/* eslint-disable default-param-last */
import { handle } from 'redux-pack';
import JwtDecode from 'jwt-decode';
import {
	SET_PATH,
	SIGN_IN_HOST,
	SET_PAGE_STATUS,
	LOAD_MEETING,
	CLEAR_MEETING,
	SET_SELECTED_MEETING,
	SET_ONBOARDING_ROUTE,
	CHECK_INVITATION,
	CHECK_SELECTED_MEETING,
	SET_ONBOARDING_COMPLETE,
	SET_ONBOARDING_TAB,
	SET_PROFILE_INFO,
	USER_CONNECTED,
	USER_DISCONNECTED,
	USERS_LIST,
	SET_WEBRTC_ID,
	SET_SCREENSHARE_WEBRTC_ID,
	USER_UPDATED,
	SET_CURRENT_VIEWERS,
	UPDATE_PASSWORD_EVENTS,
	FORGOT_PASSWORD_EVENTS,
	SET_WATCHER_ONLY,
	SET_AVATAR_POPOVER_OPEN,
	SET_SELECTED_EVENT,
	SET_IOS_OPENTOK_SESSION,
	SET_EXPECT_IOS_STREAM,
	SET_SESSION,
	SET_HELP_MENU_OPEN,
	SET_ACTIVE_HELP_SUBMENU,
	SET_HAS_UPDATED_STATE,
	SET_SELECTED_APP,
	SET_SHOWROOMS_TOKENS,
	SET_EMAIL_ENTERED,
	SIGN_IN_PRESENTER,
	SET_BACKSTAGE_COMMS_KEY_STATE,
	REFRESH_SHOWROOMS_TOKENS,
	CREATE_ACCOUNT,
	SET_APP_SIGNED_IN_WITH,
	UPDATE_PROFILE,
	GET_PROFILE,
	SET_MEETING_MODAL_OPEN,
	VERIFY_ACCOUNT,
	UPDATE_PASSWORD_GREENROOM,
	SET_SIGN_IN_ERROR,
	FORGOT_PASSWORD_GREENROOM,
	SET_MEETING_TO_EDIT,
	GET_SHOW_SESSIONS,
	ADD_SHOW_SESSION,
	EDIT_SHOW_SESSION,
	DELETE_SHOW_SESSION,
	GET_SHOW_SESSION_TYPES,
	SET_RESTRICTED_USER_STATE,
	SET_TEMP_NUMBER,
	CONFIRM_CODE,
	SET_AWAITING_PRODUCER,
	SET_JUST_VERIFIED,
	UPDATE_CHANNEL,
	CLEAR_PRODUCER_APPS,
	SYNC_SESSION_REDUCER_STATE,
	SET_ACCOUNT_DELETED,
	SET_WARNING_MODAL_SENT,
	UPDATE_APP_VERSION,
	SET_BACKSTAGE_COMMS_TARGET_WEBRTCID,
	CREATE_PLATFORM_ACCOUNT,
	SIGN_IN_LIMITED_DEVICE,
	SET_BLP_SESSION,
	USER_REMOTE_EDITED,
	SET_COMPANION_MODE,
	SET_USER_MEETING_SETTINGS,
	SET_IS_SYNERGYSKY_SESSION,
} from '../actions/session';
import * as Storage from '../../utils/local-storage';
import { APP, ONBOARDING_ROUTES } from '../session-status';
import { ICONS } from '../../components/general-components/icon';
import { ConnectedUser } from '../../connection/socket';
import {
	uniqueArray, missingPasswordError, mergeObjectArray, decodeHtmlEntities,
} from '../../utils/utils';
import { setState } from '../../utils/logger';
import { Channel } from './home';
import { getStorageItem } from '../../utils/local-storage';
import { Action, ActionGeneric, ChannelAuthMethod } from '../types/types';
import { CreatePlatformAccountResponse } from '../../connection/auth';
import { ProgramViewType } from '../types/session';
import { EntityType } from '../../types/enums';

const versionJson = require('../../version.json');

function decodeUser(token: string): GreenroomUser {
	const decodedUser = JwtDecode(token) as unknown as GreenroomUserWithPassword;

	/*
		Most users will not have a password in their tokens, but this will
		ensure the password is never included in the decoded user object
		for those that do have a password due to an older token.
	*/
	delete decodedUser.password;

	return decodedUser as GreenroomUser;
}

export enum StoredTokens {
	storedTwoFactorEvents = 'storedTwoFactorEvents'
}

const initialTwoFactor = getStorageItem(StoredTokens.storedTwoFactorEvents) ?? null;

export enum MediaTabOptionTypes {
	Files = 'Files',
	Screenshares = 'Screenshares',
	Overlays = 'Overlays',
	Recordings = 'Recordings',
	Audio = 'Audio'
}

export const MEDIA_TABS = {
	FILES: MediaTabOptionTypes.Files,
	SCREENSHARES: MediaTabOptionTypes.Screenshares,
	OVERLAYS: MediaTabOptionTypes.Overlays,
	RECORDINGS: MediaTabOptionTypes.Recordings,
	AUDIO: MediaTabOptionTypes.Audio,
};

export enum MEDIA_SETTINGS_TABS {
	CONTENT = 'Content',
	THEME = 'Theme',
	AUDIO = 'Audio',
}

export const BACKSTAGE_COMMS_KEY_STATES = {
	OFF: 0,
	CLICK: 1,
	ALL: 2,
	INDIVIDUAL: 3,
};

export const HELP_SUBMENU = {
	HELP_CENTER: 'help_center',
	KEYBOARD_SHORTCUTS: 'keyboard_shortcuts',
	PRODUCER_LEARNING_HUB: 'producer_learning_hub',
	PRESENTER_LEARNING_HUB: 'presenter_learning_hub',
	TROUBLESHOOT_VIDEO_CONNECTION: 'troubleshoot_video_connection',
	SUPPORT: 'support',
	NONE: 'none',
};

export const SHOW_SESSION_TYPES = {
	REHEARSAL: 1,
	TECH_CHECK: 2,
	RECORDING: 3,
};

export interface TutorialVideoState {
	show: boolean;
}

export interface ShowSession {
	id: number;
	session_type_id: number;
	meeting: number;
	start_time: Date;
	end_time: Date;
	title: string;
	invitations: string[];
}

export interface ShowSessionType {
	id: number;
	name: string;
}

export interface ShowroomsTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	lastRefresh: Date;
}

export interface GreenroomUser {
	tokens?: any;
	gr_user: number;
	// Added for migration strategy media+notes, but everything new should
	// be using gr_user post-decoupling
	brandlive_profile?: number;
	email: string;
	first_name: string;
	last_name: string;
	gr_producer?: number;
	tutorial_video_count: number;
	title: string;
	location: string;
	avatar?: string;
	bg_image: string;
	events_producer?: number;
	showrooms_producer?: number;
	last_modified?: Date;
	events_profile?: number | null;
	showrooms_profile?: number | null;
	allhands_profile?: number | null;
	date_created?: string;
	company?: string | null;
	allhands_producer?: number | null;
	gr_channels?: string | null; // comma separated list of channels
	verified?: number; // 0 or 1
	events3_producer?: number | null;
	is_deleted?: boolean;
	active_channel?: number | null;
	authType?: ChannelAuthMethod;
	exp?: number;
	iat?: number;
}

export interface GreenroomUserWithPassword extends GreenroomUser {
	password?: string;
}

export interface PlatformAuthToken {
    user: number;
    email: string;
    channels: number[];
    exp: number;
    authConsumer: string; // Should be "Greenroom" in our case.
    authType: ChannelAuthMethod;
    iat: number;
}

export interface Meeting {
	meeting: number;
	title: string;
	broadcast_live_end_time: string;
	broadcast_live_start_time: string;
	channel: number;
	channel_name: string;
	created: string;
	logo_white: string;
	logo_color?: string;
	logos?: any;
	meeting_id: string;
	moderation: number;
	opentok_session: string;
	primary_color: string;
	settings_json: string;
	start_time: Date;
	end_time: Date;
	updated: string;
	ustream_id: string;
	events_session_uuid: string | null;
	event_uuid: string | null;
	brandlive_event: number | null;
	showrooms_session_id: string | null;
	allhands_id: number | null;
	rtmp_endpoint: string | null;
	rtmp_endpoint_name: string | null;
	use_custom_rtmp_endpoint: number | null;
	standalone_title: string | null;
	language?: string;
	thumbnail?: string;
	registration_on?: boolean;
	channel_auth?: ChannelAuthMethod[];
	use_blp_destination?: number;
	broadcast_resolution?: string;
}

export interface UserMeetingSettings {
	gr_user: number;
	gr_meeting: number;
	settings: {
		onsite_user: boolean,
		advanced_audio_user: boolean
		disable_1080p_user: boolean,
	};
}

export interface SessionState {
	path: string,
	pageStateLoading: boolean;
	signinLoading: boolean;
	signInError: null | string | { message: string; };
	pageStateError: string | null;
	token: string | null;
	events_token: string | null;
	user: GreenroomUser | null;
	meetingLoading: boolean;
	queuedMeeting: any;
	queuedMeetingLoading: boolean;
	host: boolean;
	isEventsLegacyProducer: boolean | undefined;
	isEventsProducer: boolean | undefined;
	isStandaloneProducer: boolean | undefined;
	signed_in: boolean;
	meeting_id: string | null;
	meeting: Meeting | null;
	// this is unique to the browser, even the same user on two different
	// browsers will have different sessionIds here
	session: string | null;
	error: string | null;
	invitation: string | null;
	onboarding_route: number;
	checkingInvitation: boolean;
	invitationError: string | null;
	onboardingComplete: boolean;
	onboardingTab: number;
	joinLoading: boolean;
	joinError: string;
	connectedUsers: ConnectedUser[];
	webrtcID: string | number | null;
	screenShareWebrtcId: string | number | null;
	currentViewers: number;
	forgotPasswordLoading: boolean;
	forgotPasswordError: string;
	forgotPasswordSuccess: boolean;
	updatePasswordLoading: boolean;
	updatePasswordError: string;
	updatePasswordSuccess: boolean;
	skipApproval: boolean;
	tutorialVideoState: TutorialVideoState;
	embed: boolean;
	programView: ProgramViewType;
	shouldRefresh: boolean;
	watcher_only: boolean;
	avatarPopoverOpen: boolean;
	selectedEvent: any;
	iosOpentokSession: string | null;
	expectIosStream: boolean;
	showSessionTypes: ShowSessionType[];
	showSessions: ShowSession[];
	showSessionsLoading: boolean;
	helpMenuOpen: boolean;
	activeHelpSubmenu: string;
	backstageCommsKeyState: number;
	backstageCommsTargetWebRtcId: string;
	hasUpdatedState: boolean;
	emailEntered: string;
	selectedApp: string;
	appSignedInWith: string;
	showroomsTokens: ShowroomsTokens | undefined;
	loadingProfile: boolean;
	meetingModalOpen: boolean;
	verifying: boolean;
	justVerified: boolean;
	displayVerifyEmail: boolean;
	displayVerifyError: boolean;
	loadingProducerApps: boolean;
	loadingIsEventsProducer: boolean;
	meetingToEdit: Meeting | null;
	restrictedUserState: number;
	temp_profile: string | null;
	phone_hash: string | null;
	request_phone: boolean;
	two_factor_stored: string | null;
	eventsChannels: Channel[];
	eventsLegacyChannels: Channel[];
	grChannels: Channel[];
	awaitingProducer: boolean;
	updatingChannel: boolean;
	accountDeleted: boolean;
	warningModalSent: boolean;
	version: string;
	createPlatformAccountResponse: CreatePlatformAccountResponse | null;
	blpSession?: {
		accessToken?: string;
		refreshToken?: string;
		idToken?: string;
		signedIn: boolean;
	};
	limitedInput: boolean;
	limitedInputSmartCanvas: boolean;
	companionMode: boolean;
	userMeetingSettings: {
		onsiteUsers: number[];
		advancedAudioUsers: number[];
		r1080pUsers: number[];
	};
	isSynergyskySession: boolean;
}

export const RESTRICTED_USER_STATES = {
	NONE: 0,
	CAMERA: 1,
	MICROPHONE: 2,
	CAMERA_AND_MICROPHONE: 3,
	BLOCKED: 4,
	RECONNECTING: 5,
};

export const RESTRICTED_CONNECTION_TYPES = [
	RESTRICTED_USER_STATES.BLOCKED,
	RESTRICTED_USER_STATES.RECONNECTING,
];

export interface RestrictedUserIcon {
	type: number;
	icons: string[];
	description: string;
}

export const RESTRICTED_USER_ICONS: RestrictedUserIcon[] = [
	{ type: RESTRICTED_USER_STATES.CAMERA, icons: [ICONS.CAMERA_OFF], description: 'Cannot access camera' },
	{ type: RESTRICTED_USER_STATES.MICROPHONE, icons: [ICONS.MICROPHONE_OFF], description: 'Cannot access microphone' },
	{ type: RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, icons: [ICONS.CAMERA_OFF, ICONS.MICROPHONE_OFF], description: 'Cannot access cam & mic' },
	{ type: RESTRICTED_USER_STATES.BLOCKED, icons: [ICONS.CONNECTION], description: 'Connection blocked' },
	{ type: RESTRICTED_USER_STATES.RECONNECTING, icons: [ICONS.CONNECTION], description: 'Reconnecting' },
];

const initialEventsToken = Storage.getStorageItem('events_token');
export const initialSessionState: SessionState = {
	path: '',
	pageStateLoading: true,
	signinLoading: false,
	signInError: null,
	pageStateError: null,
	token: Storage.getStorageItem('token') || null,
	events_token: initialEventsToken || null,
	user: Storage.getStorageItem('token') ? decodeUser(Storage.getStorageItem('token')) : null,
	meetingLoading: false,
	queuedMeetingLoading: false,
	queuedMeeting: null,
	host: false,
	isEventsLegacyProducer: undefined,
	isEventsProducer: undefined,
	isStandaloneProducer: undefined,
	signed_in: !!Storage.getStorageItem('token'),
	meeting_id: null,
	meeting: null,
	session: null,
	error: null,
	invitation: null,
	onboarding_route: ONBOARDING_ROUTES?.JOIN_PRESENTER ?? 0,
	checkingInvitation: false,
	invitationError: null,
	onboardingComplete: false,
	onboardingTab: 0,
	joinLoading: false,
	joinError: '',
	connectedUsers: [],
	webrtcID: null,
	screenShareWebrtcId: null,
	currentViewers: 0,
	forgotPasswordError: '',
	forgotPasswordLoading: false,
	forgotPasswordSuccess: false,
	updatePasswordError: '',
	updatePasswordLoading: false,
	updatePasswordSuccess: false,
	skipApproval: false,
	tutorialVideoState: {
		show: false,
	},
	embed: false,
	programView: ProgramViewType.None,
	shouldRefresh: false,
	watcher_only: false,
	avatarPopoverOpen: false,
	selectedEvent: null,
	iosOpentokSession: null,
	expectIosStream: false,
	showSessionTypes: [],
	showSessions: [],
	showSessionsLoading: true,
	helpMenuOpen: false,
	activeHelpSubmenu: HELP_SUBMENU.NONE,
	backstageCommsKeyState: 0,
	backstageCommsTargetWebRtcId: '',
	hasUpdatedState: false,
	emailEntered: '',
	selectedApp: '',
	appSignedInWith: '',
	showroomsTokens: undefined,
	loadingProfile: false,
	meetingModalOpen: false,
	verifying: false,
	justVerified: false,
	displayVerifyEmail: false,
	displayVerifyError: false,
	loadingProducerApps: false,
	loadingIsEventsProducer: false,
	meetingToEdit: null,
	restrictedUserState: RESTRICTED_USER_STATES.NONE,
	temp_profile: null,
	phone_hash: null,
	request_phone: false,
	two_factor_stored: initialTwoFactor,
	eventsChannels: [],
	eventsLegacyChannels: [],
	grChannels: [],
	awaitingProducer: false,
	updatingChannel: false,
	accountDeleted: false,
	warningModalSent: false,
	version: versionJson?.version ?? 'n/a',
	createPlatformAccountResponse: null,
	blpSession: {
		signedIn: false,
	},
	limitedInput: false,
	limitedInputSmartCanvas: false,
	companionMode: false,
	userMeetingSettings: {
		onsiteUsers: [],
		advancedAudioUsers: [],
		r1080pUsers: [],
	},
	isSynergyskySession: false,
};

const actions: Map<any, Function> = new Map([
	[SET_PATH, (state: SessionState, action: Action) => ({ ...state, path: action.payload })],
	[SET_PAGE_STATUS, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			pageStateLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			pageStateLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			pageStateError: action.payload,
		}),
		success: (success) => {
			const newState = { ...success };

			const embedOnly = (
				action.payload.embed && action.payload.programView === ProgramViewType.None
			);

			if (action.payload.token && !embedOnly) {
				Storage.setStorageItem('token', action.payload.token);
			}

			if (action.payload.logout) {
				newState.signed_in = false;
				newState.user = null;
				newState.token = null;
				Storage.clearStorage().then(() => {
					/*
					Using `window.location.reload()` to log users out is a pattern
					used in this app (see the header logout for an example). Without
					making changes to the general logout prodecure, refreshing the
					page appears to be the easiest way solve the problem.
					*/
					window.location.reload();
				});
			}
			const updatedAction = { ...action };

			if (newState.session) {
				updatedAction.payload.session = newState.session;
			}

			const payloadMeeting = updatedAction.payload.meeting;
			return {
    			...newState,
    			...updatedAction.payload,
    			...(payloadMeeting?.title ? {
        			meeting: {
            			...payloadMeeting,
            			title: decodeHtmlEntities(payloadMeeting.title),
        			},
    			} : {}),
			};
		},
	})],
	[SIGN_IN_HOST, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			signinLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			signinLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			signInError: action.payload,
		}),
		success: (success) => {
			if (action.payload.message && !action.payload.message?.reset_required) {
				return {
					...success,
					signInError: action.payload.message,
				};
			} if (action.payload.error) {
				return {
					...success,
					signInError: missingPasswordError(action.payload.error),
				};
			} if (action.payload.reset_required || action.payload.message?.reset_required) {
				// Currently only Events Legacy (dashboard-API) and Events (events-api) return this flag
				let siteToResetPassword = 'the admin site';
				if (state.selectedApp === APP.EVENTS) {
					siteToResetPassword = 'https://admin.brandlive.com/authentication/forgot-password';
					if (document.location.hostname.includes('localhost')
						|| document.location.hostname.includes('brandlive-dev')) {
						siteToResetPassword = 'https://admin.brandlive-dev.com/authentication/forgot-password';
					} else if (document.location.hostname.includes('brandlive-staging')) {
						siteToResetPassword = 'https://admin.brandlive-staging.com/authentication/forgot-password';
					}
				} else if (state.selectedApp === APP.EVENTS_LEGACY) {
					siteToResetPassword = 'https://admin.brand.live';
				}

				return {
					...success,
					signInError: `Your current password has expired. Please go to ${siteToResetPassword} and reset your password.`,
				};
			} if (action.payload.needs_verification) {
				return {
					...success,
					signInError: 'It looks like you need to set a password for this account. In the Events app, please click on “Forgot Password” to set one.',
				};
			} if (action.payload.two_factor_required && action.payload.temp_profile) {
				return {
					...success,
					temp_profile: action.payload.temp_profile,
					phone_hash: action.payload.phone_hash,
					request_phone: action.payload.request_phone,
					signInError: '',
				};
			}
			if (action.payload.token) {
				Storage.setStorageItem('token', action.payload.token);

				if (action.payload.showrooms_tokens) {
					Storage.setStorageItem('showrooms-tokens', action.payload.showrooms_tokens);
				}
				if (action.payload.events_token) {
					Storage.setStorageItem('events_token', action.payload.events_token);
				}
				Storage.setStorageItem('producer-signed-in', 'true');
				Storage.setStorageItem('app-signed-in-with', state.selectedApp);

				return {
					...success,
					signInError: null,
					token: action.payload.token,
					events_token: action.payload.events_token,
					user: decodeUser(action.payload.token),
					eventsChannels: action.payload.events_channels,
					eventsLegacyChannels: action.payload.events_legacy_channels || [],
					grChannels: action.payload.gr_channels || [],
					host: true,
					showroomsTokens: action.payload.showrooms_tokens
						? JwtDecode(action.payload.showrooms_tokens) as ShowroomsTokens : undefined,
					// refresh the page. Next page state will be handled in session-status.ts
					shouldRefresh: true,
				};
			}
			return {
				...success,
				signInError: action.payload.message ? action.payload.message : 'Error attempting to sign in',
			};
		},
	})],
	[SIGN_IN_LIMITED_DEVICE, (state: SessionState, action: Action) => {
		Storage.setStorageItem('token', action.payload);
		return { ...state, token: action.payload, user: decodeUser(action.payload) };
	}],
	[SIGN_IN_PRESENTER, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			signinLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			signinLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			signInError: action.payload,
		}),
		success: (success) => {
			if (action.payload.message) {
				return {
					...success,
					signInError: action.payload.message,
				};
			} if (action.payload.error) {
				return {
					...success,
					signInError: missingPasswordError(action.payload.error),
				};
			} if (action.payload.reset_required) {
				return {
					...success,
					signInError: 'Your current password has expired. Please go to https://admin.brand.live and reset your password.',
				};
			} if (action.payload.two_factor_required && action.payload.temp_profile) {
				return {
					...success,
					temp_profile: action.payload.temp_profile,
					phone_hash: action.payload.phone_hash,
					request_phone: action.payload.request_phone,
					signInError: '',
				};
			}
			Storage.setStorageItem('token', action.payload.token);
			Storage.setStorageItem('app-signed-in-with', action.payload.app_signed_in_with);
			if (action.payload.showrooms_tokens) {
				Storage.setStorageItem('showrooms-tokens', action.payload.showrooms_tokens);
			}

			return {
				...success,
				signInError: null,
				token: action.payload.token,
				user: decodeUser(action.payload.token),
				appSignedInWith: action.payload.app_signed_in_with,
				showroomsTokens: action.payload.showrooms_tokens
					? JwtDecode(action.payload.showrooms_tokens) as ShowroomsTokens : undefined,
			};
		},
	})],
	[LOAD_MEETING, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			queuedMeetingLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			queuedMeetingLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			queuedMeeting: null,
		}),
		success: (success) => ({
			...success,
			queuedMeeting: action.payload,
		}),
	})],
	[CLEAR_MEETING, (state: SessionState) => ({ ...state, queuedMeeting: null })],
	[SET_SELECTED_MEETING, (state: SessionState, action: Action) => (
		{ ...state, meeting: action.payload, meeting_id: action.payload.meeting_id }
	)],
	[SET_ONBOARDING_ROUTE, (state: SessionState, action: Action) => (
		{ ...state, onboarding_route: action.payload }
	)],
	[CHECK_INVITATION, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			checkingInvitation: true,
		}),
		finish: (finish) => ({
			...finish,
			checkingInvitation: false,
		}),
		failure: (failure) => ({
			...failure,
			invitationError: action.payload,
		}),
		success: (success) => ({
			...success,
			invitationError: null,
			onboarding_route: ONBOARDING_ROUTES.ONBOARDING,
			meeting: action.payload,
		}),
	})],
	[CHECK_SELECTED_MEETING, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			checkingInvitation: true,
		}),
		finish: (finish) => ({
			...finish,
			checkingInvitation: false,
		}),
		failure: (failure) => ({
			...failure,
			invitationError: action.payload,
		}),
		success: (success) => ({
			...success,
			invitationError: null,
			onboarding_route: ONBOARDING_ROUTES.ONBOARDING,
		}),
	})],
	[SET_ONBOARDING_COMPLETE, (state: SessionState, action: Action) => (
		{ ...state, onboardingComplete: action.payload }
	)],
	[SET_ONBOARDING_TAB, (state: SessionState, action: Action) => (
		{ ...state, onboardingTab: action.payload }
	)],
	[SET_PROFILE_INFO, (state: SessionState, action: Action) => (
		{ ...state, user: { ...state.user, ...action.payload } }
	)],
	[GET_PROFILE, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			loadingProfile: true,
		}),
		finish: (finish) => ({
			...finish,
			loadingProfile: false,
		}),
		failure: (failure) => ({
			...failure,
			loadingProfile: false,
		}),
		success: (success) => {
			Storage.setStorageItem('token', action.payload.token);
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			const parsedPayload = JSON.parse(sanitizedPayload);

			return {
				...success,
				token: parsedPayload.token,
				user: { ...success.user, ...decodeUser(parsedPayload.token) },
				eventsChannels: parsedPayload.events_channels || [],
				eventsLegacyChannels: parsedPayload.events_legacy_channels || [],
				grChannels: parsedPayload.gr_channels || [],
				loadingProfile: false,
			};
		},
	})],
	[UPDATE_PROFILE, (state: SessionState, action: Action) => handle(state, action, {
		success: (success) => {
			Storage.setStorageItem('token', action.payload);
			return {
				...success,
				token: action.payload,
				user: decodeUser(action.payload),
			};
		},
	})],
	[CREATE_ACCOUNT, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			joinLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			joinLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			joinError: action.payload,
		}),
		success: (success) => {
			if (action.payload.message) {
				return {
					...success,
					joinError: action.payload.message,
				};
			}

			const isProducer = window.location.href.indexOf('/producer') > -1;
			const { verified } = action.payload;

			if (verified) {
				Storage.setStorageItem('token', action.payload.token);
				Storage.setStorageItem('app-signed-in-with', APP.GREENROOM);
				if (isProducer) {
					Storage.setStorageItem('producer-signed-in', 'true');
				}
			}

			// Remove verify code from the URL once we grab it to keep the URL
			// clean and not expose a code (not necessary, just thought it was a good idea)
			const url = new URL(window.location.href);
			const code = url.searchParams.get('verify');
			if (code) {
				url.searchParams.delete('verify');
			}
			const emailParam = url.searchParams.get('email');
			if (emailParam) {
				// Remove email from the URL once we grab it to keep the URL clean (not necessary,
				// just thought it was a good idea)
				url.searchParams.delete('email');
				window.history.replaceState(null, '', url.href.replace('/create-account', ''));
			}
			window.history.replaceState(null, '', url.href.replace('/create-account', ''));

			const user = decodeUser(action.payload.token);

			return {
				...success,
				token: verified ? action.payload.token : undefined,
				user: verified ? user : null,
				displayVerifyEmail: !verified,
				host: !!isProducer,
				appSignedInWith: verified ? APP.GREENROOM : '',
			};
		},
	})],
	[VERIFY_ACCOUNT, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			verifying: true,
			displayVerifyError: false,
		}),
		failure: (failure) => ({
			...failure,
			verifying: false,
			displayVerifyError: true,
		}),
		success: (success) => {
			if (!action.payload || !action.payload.token) {
				// Something happened when trying to verify the account, code was wrong, etc.
				// Return to login screen with error message and resend verification email link.
				return {
					...success,
					verifying: false,
					displayVerifyError: true,
				};
			}
			Storage.setStorageItem('token', action.payload.token);
			Storage.setStorageItem('app-signed-in-with', APP.GREENROOM);

			const url = new URL(window.location.href);
			const code = url.searchParams.get('verify');
			if (code) {
				// Remove the verification code from the URL so we don't try to re-verify when refreshing
				url.searchParams.delete('verify');
			}

			const isProducer = window.location.href.indexOf('/producer') > -1;
			if (isProducer) {
				Storage.setStorageItem('producer-signed-in', 'true');
			}

			// remove verify-account path if it exists, so it is not mistaken for a meeting id
			const isVerifyPath = window.location.href.indexOf('/verify-account') > -1;
			if (isVerifyPath) {
				window.history.replaceState(null, '', url.href.replace('/verify-account', ''));
			}

			// If presenter verified, advance to onboarding. Should see tour bubble with
			// "Account Verification Successful" prepended in the header.
			// If producer verified, advance to home container
			return {
				...success,
				verifying: false,
				justVerified: true,
				displayVerifyError: false,
				token: action.payload.token,
				user: decodeUser(action.payload.token),
				grChannels: action.payload.gr_channels || [],
				appSignedInWith: APP.GREENROOM,
				onboarding_route: isProducer
					? ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING
					: ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING,
			};
		},
	})],
	[USER_CONNECTED, (state: SessionState, action: Action) => {
		const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
		return {
			...state,
			connectedUsers: uniqueArray('socketId', [...state.connectedUsers, JSON.parse(sanitizedPayload)]),
		};
	}],
	[USER_DISCONNECTED, (state: SessionState, action: Action) => ({
		...state,
		connectedUsers: state.connectedUsers.filter(
			(user: ConnectedUser) => user.socketId !== action.payload.socketId,
		),
	})],
	[`${EntityType.connectedUsers}-delete`, (state: SessionState, action: Action) => ({
		...state,
		connectedUsers: state.connectedUsers.filter(
			(user: ConnectedUser) => user.socketId !== action.payload.entityId,
		),
	})],
	[USERS_LIST, (state: SessionState, action: Action) => {
		// when reconnecting to the socket server,
		// we want to create a new connectedUsers array instead of merging to prevent duplicate users
		const newConnectedUsers = action.payload.resetConnectedUsers
			? [...action.payload.users]
			: mergeObjectArray('socketId', 'last_modified', [...state.connectedUsers, ...action.payload.users]);

		return ({
			...state,
			connectedUsers: newConnectedUsers,
		});
	}],
	[SET_WEBRTC_ID, (state: SessionState, action: Action) => (
		{ ...state, webrtcID: action.payload }
	)],
	[SET_SCREENSHARE_WEBRTC_ID, (state: SessionState, action: Action) => (
		{ ...state, screenShareWebrtcId: action.payload }
	)],
	[USER_UPDATED, (state: SessionState, action: Action) => {
		const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
		return {
			...state,
			connectedUsers: state.connectedUsers.map((user: ConnectedUser) => {
				if (user.socketId === action.payload.socketId) {
					return { ...user, ...JSON.parse(sanitizedPayload) };
				}

				return user;
			}),
		};
	}],
	[SET_CURRENT_VIEWERS, (state: SessionState, action: Action) => (
		{ ...state, currentViewers: action.payload }
	)],
	[FORGOT_PASSWORD_EVENTS, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			forgotPasswordLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			forgotPasswordLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			forgotPasswordError: 'Unable to send password reset. Please check your inputs and try again.',
		}),
		success: (success) => ({
			...success,
			forgotPasswordSuccess: true,
			forgotPasswordError: '',
		}),
	})],
	[FORGOT_PASSWORD_GREENROOM, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			forgotPasswordLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			forgotPasswordLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			forgotPasswordError: 'Unable to send password reset. Please check your inputs and try again.',

		}),
		success: (success) => {
			if (action.payload && action.payload === 404) {
				return {
					...success,
					onboarding_route: ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
					joinError: 'noAccountForEmail',
				};
			}
			return {
				...success,
				forgotPasswordSuccess: true,
				forgotPasswordError: '',
			};
		},
	})],
	[UPDATE_PASSWORD_EVENTS, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			updatePasswordLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			updatePasswordLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			updatePasswordError: 'Unable to send password reset. Please check your inputs and try again.',
		}),
		success: (success) => ({
			...success,
			updatePasswordSuccess: true,
			updatePasswordError: '',
		}),
	})],
	[UPDATE_PASSWORD_GREENROOM, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({
			...start,
			updatePasswordLoading: true,
		}),
		finish: (finish) => ({
			...finish,
			updatePasswordLoading: false,
		}),
		failure: (failure) => ({
			...failure,
			updatePasswordError: 'Unable to send password reset. Please check your inputs and try again.',
		}),
		success: (success) => ({
			...success,
			updatePasswordSuccess: true,
			updatePasswordError: '',
		}),
	})],
	[SET_WATCHER_ONLY, (state: SessionState, action: Action) => (
		{ ...state, watcher_only: action.payload }
	)],
	[SET_AVATAR_POPOVER_OPEN, (state: SessionState, action: Action) => (
		{ ...state, avatarPopoverOpen: action.payload }
	)],
	[SET_SELECTED_EVENT, (state: SessionState, action: Action) => (
		{ ...state, selectedEvent: action.payload }
	)],
	[SET_IOS_OPENTOK_SESSION, (state: SessionState, action: Action) => (
		{ ...state, iosOpentokSession: action.payload }
	)],
	[SET_EXPECT_IOS_STREAM, (state: SessionState, action: Action) => (
		{ ...state, expectIosStream: action.payload }
	)],
	[GET_SHOW_SESSION_TYPES, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, showSessionsLoading: true }),
		finish: (finish) => ({ ...finish, showSessionsLoading: false }),
		failure: (failure) => ({ ...failure, showSessionError: 'Unable to get show session types.' }),
		success: (success) => ({ ...success, showSessionTypes: action.payload }),
	})],
	[GET_SHOW_SESSIONS, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, showSessionsLoading: true }),
		finish: (finish) => ({ ...finish, showSessionsLoading: false }),
		failure: (failure) => ({ ...failure, showSessionError: 'Unable to get show sessions.' }),
		success: (success) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			return { ...success, showSessions: JSON.parse(sanitizedPayload) };
		},
	})],
	[ADD_SHOW_SESSION, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, showSessionsLoading: true }),
		finish: (finish) => ({ ...finish, showSessionsLoading: false }),
		failure: (failure) => ({ ...failure, showSessionError: 'Error saving show session. Please try again.' }),
		success: (success) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			return { ...success, showSessions: JSON.parse(sanitizedPayload) };
		},
	})],
	[EDIT_SHOW_SESSION, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, showSessionsLoading: true }),
		finish: (finish) => ({ ...finish, showSessionsLoading: false }),
		failure: (failure) => ({ ...failure, showSessionError: 'Error saving show session. Please try again.' }),
		success: (success) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			return { ...success, showSessions: JSON.parse(sanitizedPayload) };
		},
	})],
	[DELETE_SHOW_SESSION, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, showSessionsLoading: true }),
		finish: (finish) => ({ ...finish, showSessionsLoading: false }),
		failure: (failure) => ({ ...failure, showSessionError: 'Error deleting show session. Please try again.' }),
		success: (success) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			return { ...success, showSessions: JSON.parse(sanitizedPayload) };
		},
	})],
	[SET_SESSION, (state: SessionState, action: Action) => (
		{ ...state, session: action.payload }
	)],
	[SET_HELP_MENU_OPEN, (state: SessionState, action: Action) => (
		{ ...state, helpMenuOpen: action.payload }
	)],
	[SET_ACTIVE_HELP_SUBMENU, (state: SessionState, action: Action) => (
		{ ...state, activeHelpSubmenu: action.payload }
	)],
	[SET_BACKSTAGE_COMMS_KEY_STATE, (state: SessionState, action: Action) => (
		{ ...state, backstageCommsKeyState: action.payload }
	)],
	[SET_BACKSTAGE_COMMS_TARGET_WEBRTCID, (state: SessionState, action: Action) => (
		{ ...state, backstageCommsTargetWebRtcId: action.payload }
	)],
	[SET_HAS_UPDATED_STATE, (state: SessionState, action: Action) => (
		{ ...state, hasUpdatedState: action.payload }
	)],
	[SET_SESSION, (state: SessionState, action: Action) => (
		{ ...state, session: action.payload }
	)],
	[SET_EMAIL_ENTERED, (state: SessionState, action: Action) => (
		{ ...state, emailEntered: action.payload }
	)],
	[SET_SELECTED_APP, (state: SessionState, action: Action) => (
		{ ...state, selectedApp: action.payload }
	)],
	[SET_APP_SIGNED_IN_WITH, (state: SessionState, action: Action) => (
		{ ...state, appSignedInWith: action.payload }
	)],
	[REFRESH_SHOWROOMS_TOKENS, (state: SessionState, action: Action) => handle(state, action, {
		success: (success) => {
			const refreshedTokensJWT = action.payload.tokens;
			Storage.setStorageItem('showrooms-tokens', refreshedTokensJWT);
			const refreshedTokens = JwtDecode(refreshedTokensJWT) as ShowroomsTokens;

			return {
				...success,
				showroomsTokens: refreshedTokens,
			};
		},
	})],
	[SET_SHOWROOMS_TOKENS, (state: SessionState, action: Action) => (
		{ ...state, showroomsTokens: action.payload }
	)],
	[SET_BACKSTAGE_COMMS_KEY_STATE, (state: SessionState, action: Action) => (
		{ ...state, backstageCommsKeyState: action.payload }
	)],
	[SET_MEETING_MODAL_OPEN, (state: SessionState, action: Action) => (
		{ ...state, meetingModalOpen: action.payload }
	)],
	[SET_SIGN_IN_ERROR, (state: SessionState, action: Action) => (
		{ ...state, signInError: action.payload }
	)],
	[SET_MEETING_TO_EDIT, (state: SessionState, action: Action) => (
		{ ...state, meetingToEdit: action.payload }
	)],
	[SET_RESTRICTED_USER_STATE, (state: SessionState, action: Action) => (
		{ ...state, restrictedUserState: action.payload }
	)],
	[SET_TEMP_NUMBER, (state: SessionState, action: Action) => handle(state, action, {
		success: (success) => ({
			...success, phone_hash: action.payload.phone_hash, request_phone: false,
		}),
	})],
	[CONFIRM_CODE, (state: SessionState, action: Action) => handle(state, action, {
		start: (start) => ({ ...start, signinIn: true }),
		finish: (finish) => ({ ...finish, signinIn: false }),
		failure: (failure) => ({ ...failure, signInError: action.payload.message }),
		success: (success) => {
			// Successful 2 factor auth code confirmation is the same as signing in as an Events producer
			if (action.payload?.token) {
				Storage.setStorageItem('token', action.payload.token);
				// Remember device for 30 days so don't have to re-do 2FA
				Storage.setStorageItem(
					StoredTokens.storedTwoFactorEvents,
					action.payload.storage_token,
					30 * 24,
				);
				Storage.setStorageItem('producer-signed-in', 'true');
				Storage.setStorageItem('app-signed-in-with', success.selectedApp);

				return {
					...success,
					signInError: null,
					token: action.payload.token,
					user: decodeUser(action.payload.token),
					eventsChannels: action.payload.events_channels || [],
					eventsLegacyChannels: action.payload.events_legacy_channels || [],
					grChannels: action.payload.gr_channels || [],
					// refresh the page. Next page state will be handled in session-status.ts
					shouldRefresh: true,
				};
			}
			return {
				...success,
				signInError: 'Invalid Code',
			};
		},
	})],
	[SET_AWAITING_PRODUCER, (state: SessionState, action: Action) => (
		{ ...state, awaitingProducer: action.payload }
	)],
	[SET_JUST_VERIFIED, (state: SessionState, action: Action) => (
		{ ...state, justVerified: action.payload }
	)],
	[UPDATE_CHANNEL, (state: SessionState, action: Action) => handle(state, action, {
		success: (success) => ({
			...success,
			grChannels: success.grChannels.map((channel: Channel) => {
				const updatedChannel = { ...channel };
				if (channel.channel === action.payload.channel) {
					updatedChannel.name = action.payload.name;
					updatedChannel.logo_white = action.payload.logo_white;
				}
				return updatedChannel;
			}),
			updatingChannel: false,
		}),
		failure: (failure) => ({ ...failure, updatingChannel: false }),
		start: (start) => ({ ...start, updatingChannel: true }),
		finish: (finish) => ({ ...finish, updatingChannel: false }),
	})],
	[CLEAR_PRODUCER_APPS, (state: SessionState) => ({
		...state,
		isEventsLegacyProducer: undefined,
		isEventsProducer: undefined,
		isStandaloneProducer: undefined,
	})],
	[SYNC_SESSION_REDUCER_STATE, (state: SessionState, action: Action): SessionState => (
		{ ...state, ...action.payload }
	)],
	[SET_ACCOUNT_DELETED, (state: SessionState, action: Action) => (
		{ ...state, accountDeleted: action.payload }
	)],
	// Want to warn users that comms are off before starting a stream, but
	// we don't want to warn them every time record/stream is clicked.
	[SET_WARNING_MODAL_SENT, (state: SessionState, action: Action) => (
		{ ...state, warningModalSent: action.payload }
	)],
	['UPDATE_TOKEN_MANUALLY', (state: SessionState, action: Action) => ({
		...state,
		token: action.payload,
		user: decodeUser(action.payload),
	})],
	[UPDATE_APP_VERSION, (state: SessionState, action: Action) => {
		const { version: lastVersion } = state;

		const lastVersionParts = lastVersion.split('.');
		const lastMajorRelease = Number(`${lastVersionParts[0]}.${lastVersionParts[1]}`);
		const lastMinorRelease = Number(lastVersionParts?.[2] ?? 0);

		const currentVersionParts = action.payload.split('.');
		const currentMajorRelease = Number(`${currentVersionParts[0]}.${currentVersionParts[1]}`);
		const currentMinorRelease = Number(currentVersionParts?.[2] ?? 0);

		if (currentMajorRelease > lastMajorRelease) {
			console.log(`there has been a new release please hard refresh last version: ${lastMajorRelease} to ${currentMajorRelease}`);
		} else if (
			currentMajorRelease === lastMajorRelease
			&& currentMinorRelease > lastMinorRelease
		) {
			console.log(`there has been a minor release, you may need to hard refresh: ${lastVersion} to ${action.payload}`);
		}

		return {
			...state,
			version: action.payload,
		};
	}],
	[
		CREATE_PLATFORM_ACCOUNT,
		(
			state: SessionState,
			action: ActionGeneric<CreatePlatformAccountResponse>,
		) => (
			handle(state, action, {
				start: (start) => ({
					...start,
					joinLoading: true,
				}),
				finish: (finish) => ({
					...finish,
					joinLoading: false,
				}),
				failure: (failure) => ({
					...failure,
					joinError: 'Unable to create account. Please try again.',
				}),
				success: (success) => {
					const { success: created } = action.payload;

					if (!created) {
						return {
							...success,
							joinError: 'Unable to create account. Please try again.',
						};
					}

					return {
						...success,
						displayVerifyEmail: true,
						joinError: '',
						createPlatformAccountResponse: action.payload,
					};
				},
			})
		),
	],
	[SET_BLP_SESSION, (state: SessionState, action: Action) => ({
		...state,
		blpSession: {
			...action.payload,
			signedIn: !!action.payload.refreshToken,
		},
	})],
	[USER_REMOTE_EDITED, (state: SessionState, action: Action) => {
		const update: Partial<SessionState> = {
			connectedUsers: state.connectedUsers.map((user: ConnectedUser) => {
				if (user.gr_user === action.payload.message.gr_user) {
					return { ...user, ...action.payload.message };
				}
				return user;
			}),
		};

		if (state.user?.gr_user === action.payload.message.gr_user) {
			update.user = { ...state.user, ...action.payload.message };
		}

		return {
			...state,
			...update,
		};
	}],
	[SET_COMPANION_MODE, (state: SessionState, action: Action) => (
		{ ...state, companionMode: action.payload }
	)],
	[SET_USER_MEETING_SETTINGS, (state: SessionState, action: Action) => ({
		...state,
		userMeetingSettings: {
			onsiteUsers: action.payload.onsiteUsers || [],
			advancedAudioUsers: action.payload.advancedAudioUsers || [],
			r1080pUsers: action.payload.r1080pUsers || [],
		},
	})],
	[SET_IS_SYNERGYSKY_SESSION, (state: SessionState, action: Action) => ({
		...state,
		isSynergyskySession: action.payload,
	})],
]);

export function SessionReducer(state: SessionState = initialSessionState, action: Action) {
	const newState = actions.get(action.type)?.(state, action);

	if (actions.has(action.type)) {
		setState(newState);
	}

	return newState || state;
}
